import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useCalendar } from '../contexts/CalendarContext';
import { useLocation } from 'react-router-dom';
import Calendar from '../components/Calendar';
import GroupCalendar from '../components/GroupCalendar';
import PageContainer from '../components/PageContainer';

export default function CalendarPage() {
  const { refreshCalendar } = useCalendar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);
  const [includeWeekdays, setIncludeWeekdays] = useState(false);

  // Handle navigation state from group cards
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <PageContainer
      maxWidth={false}
      sx={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pb: 2,
        pt: { xs: 2, sm: 3 },
        px: { xs: 2, md: 4 } // Add horizontal padding to prevent edge collision
      }}
    >
      {/* Compact Header Row */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap', // Allow wrapping to prevent cutoff
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Title Section */}
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Calendar
          </Typography>
          {!isMobile && (
            <Typography variant="caption" color="text.secondary">
              Manage availability
            </Typography>
          )}
        </Box>

        {/* Controls Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2
        }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                py: 0.5,
                px: 2,
                fontSize: '0.875rem'
              }
            }}
          >
            <Tab label="Personal" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Group" icon={<GroupIcon fontSize="small" />} iconPosition="start" />
          </Tabs>

          <Box sx={{ height: 24, width: 1, bgcolor: 'divider', display: { xs: 'none', sm: 'block' } }} />

          {/* Toggles & Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeWeekdays}
                  onChange={(e) => setIncludeWeekdays(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Weekdays</Typography>}
            />

            <Button
              variant="outlined"
              size="small"
              onClick={refreshCalendar}
              startIcon={<DateRangeIcon />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content Area - Takes remaining height */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 0 && (
          <Calendar includeWeekdays={includeWeekdays} fullHeight={true} />
        )}

        {activeTab === 1 && (
          <GroupCalendar
            includeWeekdays={includeWeekdays}
            selectedGroupId={location.state?.selectedGroup}
            fullHeight={true}
          />
        )}
      </Box>
    </PageContainer>
  );
}
